import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ClipboardList, Download, Trash2, Plus, FileText } from 'lucide-react';
import { COMPANY } from '../../../config/company';
import CompanyLogo from '../../CompanyLogo';

const LeadChangeOrderTab = ({
  lead,
  language,
  companyInfo,
  changeOrderStatusOptions,
  roofTypes,
  totals,
  formatCurrency,
  generatingPdf,
  changeOrderRef,
  addChangeOrder,
  updateChangeOrder,
  deleteChangeOrder,
  generateChangeOrderPDF,
  getAllLicenses
}) => {
  return (
    <>
      <Card>
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <ClipboardList className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Órdenes de Cambio' : 'Change Orders'}
            </span>
            <Button size="sm" variant="secondary" onClick={addChangeOrder} className="text-xs">
              <Plus className="h-3 w-3 mr-1" />
              {language === 'es' ? 'Nueva Orden' : 'New Change Order'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {(!lead.change_orders || lead.change_orders.length === 0) ? (
            <div className="text-center py-8 text-gray-400">
              <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{language === 'es' ? 'Sin órdenes de cambio' : 'No change orders yet'}</p>
              <Button variant="outline" onClick={addChangeOrder} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Crear Primera Orden' : 'Create First Change Order'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {lead.change_orders.map((co, index) => (
                <Card key={co.id} className="border-2">
                  <CardHeader className="py-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-bold">{co.change_order_number}</span>
                        <Badge className={changeOrderStatusOptions.find(s => s.value === co.status)?.color}>
                          {changeOrderStatusOptions.find(s => s.value === co.status)?.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => generateChangeOrderPDF(index)} disabled={generatingPdf}>
                          <Download className="h-3 w-3 mr-1" />PDF
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteChangeOrder(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">{language === 'es' ? 'Fecha' : 'Date'}</Label>
                        <Input 
                          type="date" 
                          value={co.change_order_date} 
                          onChange={(e) => updateChangeOrder(index, 'change_order_date', e.target.value)} 
                          className="text-sm" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{language === 'es' ? 'Costo Adicional' : 'Additional Cost'}</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={co.additional_cost} 
                          onChange={(e) => updateChangeOrder(index, 'additional_cost', parseFloat(e.target.value) || 0)} 
                          className="text-sm" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{language === 'es' ? 'Estado' : 'Status'}</Label>
                        <Select value={co.status} onValueChange={(v) => updateChangeOrder(index, 'status', v)}>
                          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {changeOrderStatusOptions.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{language === 'es' ? 'Descripción del Cambio' : 'Change Description'}</Label>
                      <Textarea 
                        value={co.description} 
                        onChange={(e) => updateChangeOrder(index, 'description', e.target.value)} 
                        rows={4} 
                        placeholder={language === 'es' ? 'Describa los detalles del cambio solicitado...' : 'Describe the details of the requested change...'} 
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Order Preview (shows first/selected change order) */}
      {lead.change_orders?.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="bg-gray-800 text-white">
            <CardTitle className="flex items-center text-sm">
              <FileText className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Vista Previa - Orden de Cambio' : 'Change Order Preview'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div ref={changeOrderRef} className="bg-white p-6 text-sm">
              {/* Company Header */}
              <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white p-4 -mx-6 -mt-6 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white p-2 rounded">
                      {companyInfo.logo ? (
                        <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto" />
                      ) : (
                        <CompanyLogo className="h-10 w-auto" />
                      )}
                    </div>
                    <div>
                      <h1 className="text-xl font-bold">{companyInfo.name}</h1>
                      <p className="text-amber-100 text-xs">{getAllLicenses() || companyInfo.tagline}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <p>{companyInfo.address}</p>
                    <p>{companyInfo.city}, {companyInfo.state} {companyInfo.zip}</p>
                    <p>{companyInfo.phone} | {companyInfo.email}</p>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">
                  {language === 'es' ? 'ORDEN DE CAMBIO' : 'CHANGE ORDER'}
                </h2>
                <p style={{ color: companyInfo.primaryColor }} className="font-mono">
                  {lead.change_orders[0]?.change_order_number}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 text-xs">
                <div className="border rounded p-3">
                  <p className="font-semibold text-gray-500 mb-1">
                    {language === 'es' ? 'CLIENTE' : 'CLIENT'}
                  </p>
                  <p className="font-bold">{lead.client_name}</p>
                  <p>{lead.client_address}</p>
                  <p>{lead.client_phone}</p>
                </div>
                <div className="border rounded p-3">
                  <p className="font-semibold text-gray-500 mb-1">
                    {language === 'es' ? 'PROYECTO ORIGINAL' : 'ORIGINAL PROJECT'}
                  </p>
                  <p>
                    <strong>{language === 'es' ? 'Propuesta' : 'Proposal'}:</strong>{' '}
                    {lead.proposal_id || lead.estimate_number}
                  </p>
                  <p>
                    <strong>{language === 'es' ? 'Fecha' : 'Date'}:</strong>{' '}
                    {lead.change_orders[0]?.change_order_date}
                  </p>
                  <p>
                    <strong>{language === 'es' ? 'Tipo' : 'Type'}:</strong>{' '}
                    {roofTypes.find(r => r.value === lead.roof_type)?.label}
                  </p>
                </div>
              </div>

              <div className="border rounded p-4 mb-6 bg-amber-50">
                <p className="font-semibold mb-2" style={{ color: companyInfo.primaryColor }}>
                  {language === 'es' ? 'DESCRIPCIÓN DEL CAMBIO' : 'CHANGE DESCRIPTION'}
                </p>
                <p className="whitespace-pre-wrap text-gray-700">
                  {lead.change_orders[0]?.description || (language === 'es' ? 'Sin descripción' : 'No description provided')}
                </p>
              </div>

              <div className="flex justify-end mb-6">
                <div className="w-64 border rounded overflow-hidden">
                  <div className="flex justify-between p-2 border-b">
                    <span>{language === 'es' ? 'Presupuesto Original' : 'Original Estimate'}:</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  <div className="flex justify-between p-2 border-b bg-amber-50">
                    <span>{language === 'es' ? 'Costo Adicional' : 'Additional Cost'}:</span>
                    <span className="text-amber-600">+{formatCurrency(lead.change_orders[0]?.additional_cost || 0)}</span>
                  </div>
                  <div 
                    className="flex justify-between p-3 text-lg font-bold" 
                    style={{ backgroundColor: companyInfo.primaryColor, color: 'white' }}
                  >
                    <span>{language === 'es' ? 'NUEVO TOTAL' : 'NEW TOTAL'}:</span>
                    <span>{formatCurrency(totals.grandTotal + (lead.change_orders[0]?.additional_cost || 0))}</span>
                  </div>
                </div>
              </div>

              {/* Signature Section */}
              <div className="mt-8 pt-6 border-t">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="font-semibold mb-6 text-xs">
                      {language === 'es' ? 'APROBACIÓN DEL CONTRATISTA' : 'CONTRACTOR APPROVAL'}
                    </p>
                    <div className="border-b border-gray-400 mb-1 h-8"></div>
                    <p className="text-xs">{companyInfo.name}</p>
                    <p className="text-xs mt-3">{language === 'es' ? 'Fecha' : 'Date'}: _________________</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-6 text-xs">
                      {language === 'es' ? 'APROBACIÓN DEL CLIENTE' : 'CLIENT APPROVAL'}
                    </p>
                    <div className="border-b border-gray-400 mb-1 h-8"></div>
                    <p className="text-xs">{lead.client_name}</p>
                    <p className="text-xs mt-3">{language === 'es' ? 'Fecha' : 'Date'}: _________________</p>
                  </div>
                </div>
              </div>

              <div className="text-center text-xs text-gray-500 border-t pt-4 mt-6">
                <p>{companyInfo.website} • {companyInfo.email} • {companyInfo.phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default LeadChangeOrderTab;
